<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import AutoComplete, { type AutoCompleteCompleteEvent } from 'primevue/autocomplete';
import MultiSelect from 'primevue/multiselect';
import ToggleSwitch from 'primevue/toggleswitch';
import { ArrowLeft, ArrowRight, ExternalLink, FileWarning } from '@lucide/vue';
import {
  progettoCreateSchema,
  type ClienteListItem,
  type ClientePersona,
  type ClienteSedeOperativa,
  type PartnerPersonaPicker,
  type ProgettoCreate,
  type TemplateListItem,
  type TipoDocumentoClienteRiga,
  type Utente,
} from '@evolfin/shared';
import { EvolfinApiError } from '@/api/client';
import { stringaOpzionale } from '@/utils/form';
import { useAuthStore } from '@/stores/auth';
import CampoTesto from '@/components/ui/CampoTesto.vue';
import AvvisoErrore from '@/components/ui/AvvisoErrore.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import BarraPassi from '@/components/ui/BarraPassi.vue';
import { listaClienti, listaPersone, listaSedi } from '@/features/clienti/api';
import { documentiRichiestiMancanti } from '@/api/file';
import { listaPersonePicker } from '@/features/partner/api';
import { listaTemplate } from '@/features/template/api';
import { listaUtenti } from '@/features/utenti/api';
import { useInvalidation } from '@/composables/useInvalidation';
import { creaProgetto } from '../api';

definePage({ meta: { titolo: 'Nuovo progetto' } });

const router = useRouter();
const auth = useAuthStore();
// L'elenco da cui si è arrivati non sa ancora del progetto nuovo.
const invalidate = useInvalidation('progetti');

/**
 * Wizard a 4 passi: solo il primo porta i dati obbligatori, gli
 * altri sono saltabili — "Avanti" con i campi vuoti equivale a saltare, non
 * c'è nulla da convalidare oltre al passo 1. Il codice progetto resta
 * nell'ultimo passo, mai in prima pagina (relazione).
 */
const PASSI = [
  'Dati principali',
  'Template e tipologia',
  'Operatori e partner',
  'Valori e codice',
] as const;
const passoCorrente = ref(0);
const ultimoPasso = computed(() => passoCorrente.value === PASSI.length - 1);

/* ── Cliente ──────────────────────────────────────────────────────────── */
const clienteSelezionato = ref<ClienteListItem | null>(null);
const suggerimentiCliente = ref<ClienteListItem[]>([]);
async function cercaCliente(evento: AutoCompleteCompleteEvent): Promise<void> {
  const pagina = await listaClienti({
    page: 1,
    pageSize: 20,
    order: 'asc',
    sort: 'ragioneSociale',
    q: (evento.query as string) || undefined,
  });
  suggerimentiCliente.value = pagina.items;
}

const sediCliente = ref<ClienteSedeOperativa[]>([]);
const personeCliente = ref<ClientePersona[]>([]);
const sedeIdsSelezionate = ref<number[]>([]);
const personaIdsSelezionate = ref<number[]>([]);
const opzioniPersoneCliente = computed(() =>
  personeCliente.value.map((p) => ({ id: p.id, nomeCompleto: `${p.nome} ${p.cognome}` })),
);

/**
 * Documenti che il cliente dovrebbe avere prima di una pratica e non ha. È un
 * avviso, non un blocco: «Avanti» resta disponibile, la scelta è dell'operatore.
 */
const documentiMancanti = ref<TipoDocumentoClienteRiga[]>([]);
const MAX_NOMI_MANCANTI = 3;
const elencoMancanti = computed(() => {
  const nomi = documentiMancanti.value.map((t) => t.nome);
  const visibili = nomi.slice(0, MAX_NOMI_MANCANTI).join(', ');
  const altri = nomi.length - MAX_NOMI_MANCANTI;
  return altri > 0 ? `${visibili} e ${altri === 1 ? 'un altro' : `altri ${altri}`}` : visibili;
});

watch(clienteSelezionato, async (c) => {
  sedeIdsSelezionate.value = [];
  personaIdsSelezionate.value = [];
  sediCliente.value = [];
  personeCliente.value = [];
  documentiMancanti.value = [];
  // Mentre si digita, l'AutoComplete tiene nel modello il testo scritto, non un
  // cliente: si carica solo a scelta fatta.
  if (!c || typeof c !== 'object') return;
  const [sedi, persone, mancanti] = await Promise.all([
    listaSedi(c.id),
    listaPersone(c.id),
    documentiRichiestiMancanti(c.id),
  ]);
  // Cliente cambiato mentre si aspettava: queste risposte sono del precedente.
  if (clienteSelezionato.value?.id !== c.id) return;
  [sediCliente.value, personeCliente.value, documentiMancanti.value] = [sedi, persone, mancanti];
});

// Il server porta ad Attivo il cliente di una pratica nuova: lo si dice prima, non dopo.
const statoDaPromuovere = computed(() => {
  const c = clienteSelezionato.value;
  return c && typeof c === 'object' && c.stato !== 'Attivo' ? c.stato : null;
});

/* ── Persone-partner (riferimenti esterni, opzionale) ────────────────── */
const partnerPersoneSelezionate = ref<PartnerPersonaPicker[]>([]);
const suggerimentiPartnerPersona = ref<PartnerPersonaPicker[]>([]);
async function cercaPartnerPersona(evento: AutoCompleteCompleteEvent): Promise<void> {
  suggerimentiPartnerPersona.value = await listaPersonePicker({
    q: (evento.query as string) || undefined,
  });
}
function etichettaPartnerPersona(p: PartnerPersonaPicker): string {
  return `${p.nome} ${p.cognome} · ${p.partnerRagioneSociale}`;
}

/* ── Utenti collegati (team) ──────────────────────────────────────────── */
const utentiSelezionati = ref<Utente[]>(
  auth.principal ? [auth.principal as unknown as Utente] : [],
);
const suggerimentiUtente = ref<Utente[]>([]);
async function cercaUtente(evento: AutoCompleteCompleteEvent): Promise<void> {
  const pagina = await listaUtenti({
    page: 1,
    pageSize: 20,
    order: 'asc',
    sort: 'cognome',
    stato: 'attivi',
    q: (evento.query as string) || undefined,
  });
  const giaScelti = new Set(utentiSelezionati.value.map((u) => u.id));
  suggerimentiUtente.value = pagina.items.filter((u) => !giaScelti.has(u.id));
}
function etichettaUtente(u: Utente): string {
  return `${u.nome} ${u.cognome}`;
}

/* ── Template d'origine (opzionale, cercato) ──────────────────────────── */
const templateSelezionato = ref<TemplateListItem | null>(null);
const suggerimentiTemplate = ref<TemplateListItem[]>([]);
async function cercaTemplate(evento: AutoCompleteCompleteEvent): Promise<void> {
  const pagina = await listaTemplate({
    page: 1,
    pageSize: 20,
    order: 'asc',
    sort: 'nome',
    attivo: true,
    q: (evento.query as string) || undefined,
  });
  suggerimentiTemplate.value = pagina.items;
}

/**
 * Tipologia pratica: senza template è sempre libera. Con un template scelto,
 * deriva automaticamente dal suo nome ed è bloccata finché non si attiva
 * "Personalizza" — che serve solo a sbloccarla, non un modo alternativo di
 * scriverla.
 */
const personalizzaTipologia = ref(false);
const tipologiaPratica = ref('');
const tipologiaModificabile = computed(
  () => !templateSelezionato.value || personalizzaTipologia.value,
);
watch(templateSelezionato, (t) => {
  personalizzaTipologia.value = false;
  tipologiaPratica.value = t ? t.nome : '';
});

/* ── Campi scalari ────────────────────────────────────────────────────── */
const form = reactive({
  nome: '',
  descrizione: '',
  valoreTeoricoInvestimento: '',
  valoreTeoricoAgevolazione: '',
  codiceInterno: '',
});

const erroriCampo = ref<Record<string, string[]>>({});
const erroreGenerale = ref<string | null>(null);
const salvataggio = ref(false);
const primoErrore = (k: string) => erroriCampo.value[k]?.[0];

function raccogliErrori(issues: readonly { path: PropertyKey[]; message: string }[]): void {
  for (const i of issues) {
    (erroriCampo.value[i.path.map(String).join('.')] ??= []).push(i.message);
  }
}

/** Valida solo il passo 1 (obbligatorio): gli altri sono saltabili per definizione. */
function validaPassoPrincipale(): boolean {
  erroriCampo.value = {};
  let ok = true;
  if (!form.nome.trim()) {
    erroriCampo.value.nome = ['Il nome è obbligatorio'];
    ok = false;
  }
  if (!clienteSelezionato.value) {
    erroriCampo.value.clienteId = ['Seleziona un cliente'];
    ok = false;
  }
  if (!sedeIdsSelezionate.value.length) {
    erroriCampo.value.sedeClienteIds = ['Indica almeno una sede del cliente'];
    ok = false;
  }
  return ok;
}

/**
 * Gli operatori stanno in un passo saltabile ma restano obbligatori: il campo
 * nasce già con l'utente corrente, quindi saltare il passo va bene — svuotarlo no.
 */
function validaOperatori(): boolean {
  if (utentiSelezionati.value.length) return true;
  erroriCampo.value = { utenteIds: ['Indica almeno un utente collegato'] };
  return false;
}

function avanti(): void {
  if (passoCorrente.value === 0 && !validaPassoPrincipale()) return;
  if (passoCorrente.value === 2 && !validaOperatori()) return;
  erroriCampo.value = {};
  erroreGenerale.value = null;
  if (!ultimoPasso.value) passoCorrente.value += 1;
}

function indietro(): void {
  erroriCampo.value = {};
  erroreGenerale.value = null;
  if (passoCorrente.value > 0) passoCorrente.value -= 1;
}

function vaiAlPasso(indice: number): void {
  if (indice <= passoCorrente.value) passoCorrente.value = indice;
}

function costruisciPayload(): ProgettoCreate {
  return {
    codiceInterno: stringaOpzionale(form.codiceInterno),
    nome: form.nome,
    descrizione: stringaOpzionale(form.descrizione),
    clienteId: clienteSelezionato.value?.id as number,
    templateOrigineId: templateSelezionato.value?.id,
    tipologiaPratica: stringaOpzionale(tipologiaPratica.value),
    stato: 'Attivo',
    valoreTeoricoInvestimento: form.valoreTeoricoInvestimento
      ? Number(form.valoreTeoricoInvestimento)
      : undefined,
    valoreTeoricoAgevolazione: form.valoreTeoricoAgevolazione
      ? Number(form.valoreTeoricoAgevolazione)
      : undefined,
    sedeClienteIds: sedeIdsSelezionate.value,
    clientePersonaIds: personaIdsSelezionate.value,
    partnerPersonaIds: partnerPersoneSelezionate.value.map((p) => p.id),
    utenteIds: utentiSelezionati.value.map((u) => u.id),
  };
}

async function salva(): Promise<void> {
  if (!validaPassoPrincipale()) {
    passoCorrente.value = 0;
    return;
  }
  if (!validaOperatori()) {
    passoCorrente.value = 2;
    return;
  }
  erroreGenerale.value = null;

  const esito = progettoCreateSchema.safeParse(costruisciPayload());
  if (!esito.success) {
    raccogliErrori(esito.error.issues);
    return;
  }

  salvataggio.value = true;
  try {
    const creato = await creaProgetto(esito.data);
    await invalidate();
    await router.push(`/progetti/${creato.id}`);
  } catch (errore) {
    if (errore instanceof EvolfinApiError) {
      erroriCampo.value = { ...erroriCampo.value, ...errore.erroriCampo };
      erroreGenerale.value = errore.message;
    } else {
      erroreGenerale.value = 'Errore imprevisto. Riprova.';
    }
  } finally {
    salvataggio.value = false;
  }
}
</script>

<template>
  <div class="mx-auto flex w-full max-w-3xl flex-col gap-6">
    <div>
      <button
        type="button"
        class="flex w-fit items-center gap-1.5 text-[13px] text-ink-secondary transition-colors hover:text-ink"
        @click="router.push('/progetti')"
      >
        <ArrowLeft :size="15" :stroke-width="1.9" /> Progetti
      </button>
      <h1 class="mt-3 font-display text-[28px] leading-tight font-semibold">Nuovo progetto</h1>
    </div>

    <BarraPassi :passi="PASSI" :corrente="passoCorrente" @vai="vaiAlPasso" />

    <form class="flex flex-col gap-5" novalidate @submit.prevent="avanti">
      <AvvisoErrore v-if="erroreGenerale" :messaggio="erroreGenerale" />

      <!-- Passo 1: dati principali (obbligatorio) -->
      <section v-if="passoCorrente === 0" class="card flex flex-col gap-4 p-5">
        <h2 class="text-[11px] font-semibold tracking-[0.07em] text-ink-muted uppercase">
          Dati principali
        </h2>

        <CampoTesto
          v-model="form.nome"
          etichetta="Nome del progetto"
          richiesto
          :errore="primoErrore('nome')"
        />
        <CampoTesto v-model="form.descrizione" etichetta="Descrizione" />

        <div class="flex flex-col gap-4 border-t border-line-soft pt-4">
          <h3 class="text-[11px] font-semibold tracking-[0.07em] text-ink-muted uppercase">
            Cliente
          </h3>

          <div class="flex flex-col gap-1.5">
            <label class="text-[13px] font-medium text-ink"
              >Cliente<span class="text-[var(--color-danger-text)]"> *</span></label
            >
            <AutoComplete
              v-model="clienteSelezionato"
              data-key="id"
              :suggestions="suggerimentiCliente"
              :option-label="(c: ClienteListItem) => c.ragioneSociale"
              placeholder="Cerca per ragione sociale o codice fiscale…"
              :min-length="0"
              :delay="350"
              complete-on-focus
              force-selection
              dropdown
              fluid
              @complete="cercaCliente"
            >
              <template #option="{ option }: { option: ClienteListItem }">
                <div class="min-w-0">
                  <p class="truncate text-[13px] font-medium text-ink">
                    {{ option.ragioneSociale }}
                  </p>
                  <p class="truncate text-[12px] text-ink-muted tabular-nums">
                    {{ option.codiceFiscale }}
                  </p>
                </div>
              </template>
            </AutoComplete>
            <p v-if="primoErrore('clienteId')" class="text-[12px] text-[var(--color-danger-text)]">
              {{ primoErrore('clienteId') }}
            </p>
            <p v-else-if="statoDaPromuovere" class="text-[12px] text-ink-muted">
              Alla creazione il cliente passa da {{ statoDaPromuovere }} ad Attivo.
            </p>
          </div>

          <div
            v-if="clienteSelezionato && documentiMancanti.length"
            class="flex items-start gap-2.5 rounded-md border border-[var(--color-warning)]/40 bg-[var(--surface-warning-soft)] px-3 py-2.5 text-[13px] text-ink"
          >
            <FileWarning :size="16" :stroke-width="1.9" class="mt-px shrink-0" aria-hidden="true" />
            <div class="flex min-w-0 flex-col gap-1">
              <p>
                {{ documentiMancanti.length === 1 ? 'Documento mancante:' : 'Documenti mancanti:' }}
                <span class="font-medium">{{ elencoMancanti }}</span
                >.
              </p>
              <!-- Nuova scheda: il wizard già compilato non va perso per caricare un file. -->
              <a
                :href="`/clienti/${clienteSelezionato.id}?tab=documenti`"
                target="_blank"
                rel="noopener"
                class="inline-flex w-fit items-center gap-1 font-medium text-[var(--color-primary)] hover:underline"
              >
                Apri i documenti del cliente
                <ExternalLink :size="13" :stroke-width="1.9" aria-hidden="true" />
              </a>
            </div>
          </div>

          <div v-if="clienteSelezionato" class="grid gap-4 sm:grid-cols-2">
            <div class="flex flex-col gap-1.5">
              <label class="text-[13px] font-medium text-ink"
                >Unità locali coinvolte<span class="text-[var(--color-danger-text)]">
                  *</span
                ></label
              >
              <MultiSelect
                v-model="sedeIdsSelezionate"
                :options="sediCliente"
                option-label="etichetta"
                option-value="id"
                placeholder="Seleziona le sedi…"
                empty-message="Nessuna sede per questo cliente."
                :show-toggle-all="false"
                fluid
              />
              <p
                v-if="primoErrore('sedeClienteIds')"
                class="text-[12px] text-[var(--color-danger-text)]"
              >
                {{ primoErrore('sedeClienteIds') }}
              </p>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[13px] font-medium text-ink">Personale cliente coinvolto</label>
              <MultiSelect
                v-model="personaIdsSelezionate"
                :options="opzioniPersoneCliente"
                option-label="nomeCompleto"
                option-value="id"
                placeholder="Seleziona le persone…"
                empty-message="Nessuna persona per questo cliente."
                :show-toggle-all="false"
                fluid
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Passo 2: template e tipologia (saltabile) -->
      <section v-else-if="passoCorrente === 1" class="card flex flex-col gap-4 p-5">
        <h2 class="text-[11px] font-semibold tracking-[0.07em] text-ink-muted uppercase">
          Template e tipologia
        </h2>
        <div class="flex flex-col gap-1.5">
          <label class="text-[13px] font-medium text-ink">Template d'origine</label>
          <AutoComplete
            v-model="templateSelezionato"
            data-key="id"
            :suggestions="suggerimentiTemplate"
            :option-label="(t: TemplateListItem) => t.nome"
            placeholder="Cerca un template, o lascia vuoto…"
            :min-length="0"
            :delay="350"
            complete-on-focus
            force-selection
            dropdown
            show-clear
            fluid
            @complete="cercaTemplate"
          />
          <p class="text-[12px] text-ink-muted">
            Copia fasi e struttura documentale sul progetto; da quel momento sono indipendenti.
            Senza template, la struttura parte vuota.
          </p>
        </div>

        <div class="flex flex-col gap-1.5">
          <div class="flex items-center justify-between gap-2">
            <label class="text-[13px] font-medium text-ink">Tipologia pratica</label>
            <label
              v-if="templateSelezionato"
              class="flex items-center gap-2 text-[12px] font-medium text-ink-secondary"
            >
              Personalizza
              <ToggleSwitch v-model="personalizzaTipologia" />
            </label>
          </div>
          <CampoTesto
            v-model="tipologiaPratica"
            etichetta="Tipologia"
            :disabled="!tipologiaModificabile"
          />
          <p class="text-[12px] text-ink-muted">
            <template v-if="templateSelezionato && !personalizzaTipologia">
              Derivata dal template scelto. Attiva "Personalizza" per scriverla a mano.
            </template>
            <template v-else>
              Dopo la creazione, si potrà correggere solo con un'azione dedicata.
            </template>
          </p>
        </div>
      </section>

      <!-- Passo 3: operatori e partner (saltabile) -->
      <section v-else-if="passoCorrente === 2" class="card flex flex-col gap-4 p-5">
        <h2 class="text-[11px] font-semibold tracking-[0.07em] text-ink-muted uppercase">
          Operatori e partner
        </h2>

        <div class="flex flex-col gap-1.5">
          <label class="text-[13px] font-medium text-ink"
            >Utenti collegati<span class="text-[var(--color-danger-text)]"> *</span></label
          >
          <AutoComplete
            v-model="utentiSelezionati"
            multiple
            data-key="id"
            :suggestions="suggerimentiUtente"
            :option-label="etichettaUtente"
            placeholder="Cerca per nome o email…"
            :min-length="0"
            :delay="350"
            complete-on-focus
            force-selection
            dropdown
            fluid
            @complete="cercaUtente"
          >
            <template #option="{ option }: { option: Utente }">
              <span class="text-[13px]">{{ option.nome }} {{ option.cognome }}</span>
            </template>
          </AutoComplete>
          <p v-if="primoErrore('utenteIds')" class="text-[12px] text-[var(--color-danger-text)]">
            {{ primoErrore('utenteIds') }}
          </p>
          <p v-else class="text-[12px] text-ink-muted">
            Sono gli operatori Evolfin che seguono il progetto: solo loro potranno vederlo e
            ricevere fasi, scadenze e task.
          </p>
        </div>

        <div class="flex flex-col gap-1.5 border-t border-line-soft pt-4">
          <label class="text-[13px] font-medium text-ink">Persone-partner</label>
          <AutoComplete
            v-model="partnerPersoneSelezionate"
            multiple
            data-key="id"
            :suggestions="suggerimentiPartnerPersona"
            :option-label="etichettaPartnerPersona"
            placeholder="Cerca per nome o partner…"
            :min-length="0"
            :delay="350"
            complete-on-focus
            force-selection
            dropdown
            fluid
            @complete="cercaPartnerPersona"
          />
          <p class="text-[12px] text-ink-muted">
            Vuoto ⇒ il progetto è "Diretto"; con almeno un riferimento, è segnalato/esterno.
          </p>
        </div>
      </section>

      <!-- Passo 4: valori e codice (saltabile) -->
      <section v-else class="card flex flex-col gap-4 p-5">
        <h2 class="text-[11px] font-semibold tracking-[0.07em] text-ink-muted uppercase">
          Valori e codice
        </h2>
        <div class="grid gap-4 sm:grid-cols-2">
          <CampoTesto
            v-model="form.valoreTeoricoInvestimento"
            etichetta="Valore teorico investimento (€)"
            inputmode="numeric"
            tabulare
          />
          <CampoTesto
            v-model="form.valoreTeoricoAgevolazione"
            etichetta="Valore teorico agevolazione (€)"
            inputmode="numeric"
            tabulare
          />
        </div>
        <CampoTesto
          v-model="form.codiceInterno"
          etichetta="Codice progetto"
          nota="Lasciato vuoto, viene generato automaticamente. Sempre modificabile in seguito."
        />
      </section>

      <div class="flex items-center justify-between gap-3">
        <PulsanteAzione v-if="passoCorrente > 0" variante="silenzioso" @click="indietro">
          Indietro
        </PulsanteAzione>
        <PulsanteAzione v-else variante="silenzioso" @click="router.push('/progetti')">
          Annulla
        </PulsanteAzione>

        <PulsanteAzione v-if="!ultimoPasso" tipo="submit">
          Continua
          <template #icona><ArrowRight :size="15" :stroke-width="2" /></template>
        </PulsanteAzione>
        <PulsanteAzione v-else :caricamento="salvataggio" @click="salva">
          Crea progetto
        </PulsanteAzione>
      </div>
    </form>
  </div>
</template>
